import { EMPTY, of as observableOf } from 'rxjs';

import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  SimpleChange,
  ViewEncapsulation,
} from '@angular/core';
import { ConfirmationOrder } from '@models/confirmation-order';
import { OperationError } from '@models/operationError.class';
import { GENERAL_CODE_CALLBACKS, TradeInfo } from '@models/trade-info.class';
import { Store } from '@ngrx/store';
import { catchError } from 'rxjs/operators';
import {
  ALGORITHM_TYPE,
  AppState,
  GEOLOCATION_NOTIFICATION_MESSAGE,
  OPERATION_MODAL_OPENED,
  OPERATION_RESET,
  ORDER_TYPE,
  ORDER_TYPE_LIST,
  TRACKING_EVENTS,
  USER_ROLES,
  VIGENCIA,
} from 'src/app';
import {
  NEW_RELIC_KNOWN_ERRORS,
  NewRelicLogger,
} from 'src/components/shared/newrelicLogger/newRelicLogger.class';
import {
  GeolocationService,
  NotificationService,
  OperationService,
  PatronService,
} from 'src/core';
import {
  AnalyticsTrackingService,
  TrackingOrderType,
} from 'src/core/services/analyticsTracking.service';
import { ChangeCharacterFromIssue } from 'src/shared/utility/change-character';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'onecancelanother-order',
  templateUrl: './oneCancelAnother.component.html',
  styleUrls: ['./oneCancelAnother.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class OneCancelAnotherOrderComponent implements OnChanges {
  @Input() selectedPrice: number;
  @Input() currentContract: any;
  @Input() currentIssue: any;
  @Input() isReadAndWrite: boolean;
  @Input() hasIssue: boolean;
  @Input() replaceTitles: number;
  @Input() isInternalUser: boolean;
  @Input() userRole: any;
  @Input() isUserGlobal: boolean;
  @Input() mfaType: number;
  @Input() readToken: string;
  @Input() elPatronData: any;
  @Input() isDiscretional: any;
  @Input() isGeolocationEnabled: boolean;
  @Input() isPositionMocked: boolean;
  @Input() shouldStartLoaderOnOperationAttempt: boolean;
  @Input() shouldNotifyGeolocationOnOperationAttempt: boolean;

  public confirmationOrder: ConfirmationOrder;
  public isBuyButtonLoading: boolean;
  public isSellButtonLoading: boolean;
  private operationCallback: () => void;

  public selectedOrder: any = {};

  private STOP_EN_LINEA = 8;
  private STOP_LIMITADO = 9;
  private TRAILING_STOP = 10;

  public ordenType: any = [
    { id: 8, name: 'Stop en Línea' },
    { id: 9, name: 'Stop Limitado' },
    { id: 10, name: 'Trailing Stop' },
  ];

  public vigencia = VIGENCIA;

  private price = 0;
  private priceStop = 0;
  private priceLimit = 0;
  private porcentaje = 0;
  private duration = 1;
  public titles = 0;
  private order: any = {};

  private saleType: string;
  public showConfirmation = false;
  private lockConfirmation = false;

  private token: string;

  public defaultDuration = { id: 1, name: '1 día' };

  public canOperate = true;
  public canModifyDuration = true;

  private lockCancel = false;
  private bitBuy: boolean;

  private tradeInfo: TradeInfo;

  constructor(
    private el: ElementRef,
    private notificationService: NotificationService,
    private operationService: OperationService,
    private store: Store<AppState>,
    private patronService: PatronService,
    private analyticsTrackingService: AnalyticsTrackingService,
    private newRelicLogger: NewRelicLogger,
    private geolocationService: GeolocationService,
  ) {}

  reset() {
    this.selectedOrder = {};
    this.price = 0;
    this.priceStop = 0;
    this.priceLimit = 0;
    this.porcentaje = 0;
    this.duration = 0;
    this.titles = 0;
    this.order = {};
    this.saleType = '';
    this.showConfirmation = false;
    this.lockConfirmation = false;
    this.token = '';
    this.replaceTitles = 0;
    this.selectedPrice = 0;

    this.defaultDuration = { id: 1, name: '1 día' };

    this.store.dispatch({
      type: OPERATION_RESET,
      payload: { reset: true },
    });

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  changeToken(event: any) {
    if (event.length > 6) {
      this.lockConfirmation = true;
      return false;
    } else if (event.length === 6) {
      this.lockConfirmation = false;
    } else {
      this.lockConfirmation = true;
      this.token = event;
    }
  }

  ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (
      changes.shouldStartLoaderOnOperationAttempt &&
      !changes.shouldStartLoaderOnOperationAttempt.currentValue &&
      this.operationCallback
    ) {
      /**
       * TODO: This doesn't work yet, due to a bug that re-renders the
       * entire operation puppet after fetching the geolocation, and
       * this wipes out the state of the puppet, so even if
       * the callback gets called, all the variables get their default
       * value right after, and the previous changes are never rendered.
       *
       * This should be replicated on every OrderType once this bug
       * gets fixed.
       */
      this.operationCallback();
      return;
    }

    const { userRole, isUserGlobal } = changes;

    if (userRole !== undefined && userRole.currentValue !== undefined) {
      this.canOperate = !this.isInternalUser;
      this.canOperate =
        userRole.currentValue === USER_ROLES.READONLY ? false : this.canOperate;
    }

    if (
      userRole !== undefined &&
      userRole.currentValue !== undefined &&
      isUserGlobal !== undefined &&
      isUserGlobal.currentValue !== undefined
    ) {
      this.canModifyDuration =
        userRole.currentValue === USER_ROLES.ADVISOR &&
        isUserGlobal.currentValue
          ? false
          : true;
    }

    if (changes.selectedPrice !== undefined) {
      if (changes.selectedPrice.currentValue !== undefined) {
        this.setPrice(changes.selectedPrice.currentValue);
      }
    }

    if (changes.currentIssue !== undefined) {
      if (changes.currentIssue.currentValue !== undefined) {
        this.isOrderReady(true);
      }
    }

    if (changes.replaceTitles !== undefined) {
      if (changes.replaceTitles.currentValue !== undefined) {
        if (changes.replaceTitles.currentValue > 0) {
          this.titles = changes.replaceTitles.currentValue;
        }
      }
    }
  }

  setTitles(event: any) {
    this.titles = event;
    this.isValidTitles();
    this.isOrderReady(true);
  }

  setDuration(event: any) {
    this.duration = event.id;

    if (!this.canModifyDuration) {
      this.duration = 1;
    }

    this.isValidDuration();
    this.isOrderReady(true);
  }

  setPrice(event: any) {
    const price = Number(event);
    const decimalsToFix = price < 1 ? 3 : 2;
    this.price = Number(price.toFixed(decimalsToFix));
    this.isValidPrice(false, this.price, '.price');
    this.isOrderReady(true);
  }

  setPriceStop(event: any) {
    if (event.length < 1) {
      this.priceStop = 0;
    } else {
      this.priceStop = event < 1 ? event.toFixed(3) : event.toFixed(2);
    }
    this.isValidPrice(false, this.priceStop, '.prc-stop');
    this.isOrderReady(true);
  }

  setPriceLimit(event: any) {
    if (event.length < 1) {
      this.priceLimit = 0;
    } else {
      this.priceLimit = event < 1 ? event.toFixed(3) : event.toFixed(2);
    }
    this.isValidPrice(false, this.priceLimit, '.prc-limite');
    this.isOrderReady(true);
  }

  setPorcentaje(event: any) {
    this.porcentaje = event;
    this.isValidPrice(false, this.porcentaje, '.porcentaje');
    this.isOrderReady(true);
  }

  setIntraOrderType(event: any) {
    this.selectedOrder = event;
    this.isValidOrder(true);
    this.isOrderReady(true);
  }

  sendOperativeMessage(titleText: string, messageText: string) {
    let notificationMessage: any = {};

    notificationMessage = {
      message: messageText || 'Necesitas ingresar el token',
      title: titleText || 'Token Necesario',
    };
    this.notificationService.showWarning(notificationMessage);
  }

  resetValues() {
    const parent = this.el.nativeElement;

    parent.querySelector('.btn-compra').classList.remove('active');
    parent.querySelector('.btn-venta').classList.remove('active');

    this.priceStop = 0;
    this.priceLimit = 0;
    this.porcentaje = 0;
  }

  isValidDuration(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.duration <= 0) {
      // if (!onlyCheck) {
      //   parent.querySelector('.duration input').classList.add('invalid');
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   parent.querySelector('.duration input').classList.remove('invalid');
      // }
      return true;
    }
  }

  isValidTitles(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.titles <= 0 || isNaN(this.titles)) {
      // if (!onlyCheck) {
      //   parent.querySelector('.order-titles').classList.add('invalid');
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   parent.querySelector('.order-titles').classList.remove('invalid');
      // }     // if (!onlyCheck) {
      //   parent.querySelector('.order-titles').classList.remove('invalid');
      // }
      return true;
    }
  }

  isValidPrice(onlyCheck: boolean = false, price: number, searchClass: string) {
    const parent = this.el.nativeElement;
    if (price <= 0 || isNaN(price)) {
      // if (!onlyCheck) {
      //   if (parent.querySelector(searchClass) !== null) {
      //     parent.querySelector(searchClass).classList.add('invalid');
      //   }
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   if (parent.querySelector(searchClass) !== null) {
      //     parent.querySelector(searchClass).classList.remove('invalid');
      //   }
      // }
      return true;
    }
  }

  isValidOrder(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.selectedOrder.id === undefined) {
      // this.resetValues();
      // if (!onlyCheck) {
      //   parent.querySelector('.tipo-operacion input').classList.add('invalid');
      // }
      return false;
    } else {
      // if (!onlyCheck) {
      //   parent.querySelector('.tipo-operacion input').classList.remove('invalid');
      // }
      return true;
    }
  }

  isValidIssue() {
    if (this.currentIssue !== undefined) {
      if (this.currentIssue.issueID !== undefined) {
        return true;
      }
    }
    return false;
  }

  isOrderReady(onlyCheck: boolean = false) {
    if (!this.canOperate) {
      return;
    }

    switch (this.selectedOrder.id) {
      case this.STOP_EN_LINEA:
        return this.isStopEnLineaReady(onlyCheck);
      case this.STOP_LIMITADO:
        return this.isStopLimitadoReady(onlyCheck);
      case this.TRAILING_STOP:
        return this.isTrailingStopReady(onlyCheck);
      default:
        return this.isDefaultReady(onlyCheck);
    }
  }

  isDefaultReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;

    if (
      !this.isValidTitles(onlyCheck) ||
      !this.isValidDuration(onlyCheck) ||
      !this.isValidOrder(onlyCheck) ||
      !this.isValidPrice(onlyCheck, this.price, '.price') ||
      !this.isValidIssue()
    ) {
      return false;
    } else {
      return true;
    }
  }

  isStopEnLineaReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;
    if (this.selectedOrder.id !== this.STOP_EN_LINEA) {
      return true;
    } else if (
      !this.isDefaultReady(onlyCheck) ||
      !this.isValidPrice(onlyCheck, this.priceStop, '.prc-stop-enlinea')
    ) {
      if (
        parent.querySelector('.btn-compra') !== null &&
        parent.querySelector('.btn-venta') !== null
      ) {
        parent.querySelector('.btn-compra').classList.remove('active');
        parent.querySelector('.btn-venta').classList.remove('active');
      }
      return false;
    } else {
      if (parent.querySelector('.btn-compra') !== null) {
        parent.querySelector('.btn-compra').classList.add('active');
      }
      if (this.hasIssue || this.isPositionMocked) {
        parent.querySelector('.btn-venta').classList.add('active');
      }
      return true;
    }
  }

  buildStopEnLineaOrder(orderType: string, algorithm: any) {
    const CAPITAL_ORDER: number =
      orderType === 'venta' ? ORDER_TYPE.VENTANORMAL : ORDER_TYPE.COMPRANORMAL;
    const ALGOTRADING: string =
      orderType === 'venta' ? 'SOAlgoTradingTypeId' : 'FOAlgoTradingTypeId';
    const PRICE: string = orderType === 'venta' ? 'price' : 'SOPrice';
    const STOP: string = orderType === 'venta' ? 'SOStop' : 'stop';

    this.order = {
      contractId: this.currentContract,
      duration: this.duration,
      algoTradingTypeId: ALGORITHM_TYPE.ONECANCELANOTHER,
      orders: [
        {
          issueId: this.currentIssue.issueID,
          quantity: this.titles,
          capitalOrderTypeId: CAPITAL_ORDER,
          instrumentType: this.currentIssue.instrumentType,
          algoTradingTypeId: ALGORITHM_TYPE.ONECANCELANOTHER,
        },
      ],
    };
    this.order.orders[0][ALGOTRADING] = algorithm;
    this.order.orders[0][PRICE] = this.price;
    this.order.orders[0][STOP] = this.priceStop;

    this.confirmationOrder = {
      issueID: this.order.orders[0].issueId,
      issueName: this.currentIssue.issueName,
      stopPrice: this.order.orders[0][STOP],
      targetPrice: this.order.orders[0][PRICE],
      titles: this.order.orders[0].quantity,
      vigency: this.order.duration,
      type: 'One cancels another (OCA)',
      isBuy: this.bitBuy,
      typeId: ORDER_TYPE_LIST.oca,
      algorithm: 'Stop en línea',
      algorithmTypeId: algorithm,
    };
  }

  tryStopEnLinea(orderType: string, algorithm: any) {
    if (this.isStopEnLineaReady()) {
      this.buildStopEnLineaOrder(orderType, algorithm);
      this.setShowConfirmation(true);
    } else {
      this.setShowConfirmation();
    }
  }

  isStopLimitadoReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;

    if (this.selectedOrder.id !== this.STOP_LIMITADO) {
      return true;
    } else if (
      !this.isDefaultReady(onlyCheck) ||
      !this.isValidPrice(onlyCheck, this.priceStop, '.prc-stop-stoplimitado') ||
      !this.isValidPrice(onlyCheck, this.priceLimit, '.prc-limite')
    ) {
      if (
        parent.querySelector('.btn-compra') !== null &&
        parent.querySelector('.btn-venta') !== null
      ) {
        parent.querySelector('.btn-compra').classList.remove('active');
        parent.querySelector('.btn-venta').classList.remove('active');
      }
      return false;
    } else {
      parent.querySelector('.btn-compra').classList.add('active');
      if (this.hasIssue || this.isPositionMocked) {
        parent.querySelector('.btn-venta').classList.add('active');
      }
      return true;
    }
  }

  buildStopLimitadoOrder(orderType: string, algorithm: any) {
    const CAPITAL_ORDER: number =
      orderType === 'venta' ? ORDER_TYPE.VENTANORMAL : ORDER_TYPE.COMPRANORMAL;
    const ALGOTRADING: string =
      orderType === 'venta' ? 'SOAlgoTradingTypeId' : 'FOAlgoTradingTypeId';
    const STOP: string = orderType === 'venta' ? 'SOStop' : 'stop';
    const vprice: string = orderType === 'venta' ? 'price' : 'SOPrice';
    const vSOPrice: string = orderType === 'venta' ? 'SOPrice' : 'price';

    this.order = {
      contractId: this.currentContract,
      duration: this.duration,
      algoTradingTypeId: ALGORITHM_TYPE.ONECANCELANOTHER,
      orders: [
        {
          issueId: new ChangeCharacterFromIssue().ChangeToAmpersand(
            this.currentIssue.issueID,
          ),
          quantity: this.titles,
          capitalOrderTypeId: CAPITAL_ORDER,
          instrumentType: this.currentIssue.instrumentType,
          algoTradingTypeId: ALGORITHM_TYPE.ONECANCELANOTHER,
        },
      ],
    };
    this.order.orders[0][vSOPrice] = this.priceLimit;
    this.order.orders[0][vprice] = this.price;
    this.order.orders[0][ALGOTRADING] = algorithm;
    this.order.orders[0][STOP] = this.priceStop;

    this.confirmationOrder = {
      issueID: this.order.orders[0].issueId,
      issueName: this.currentIssue.issueName,
      stopPrice: this.order.orders[0][STOP],
      limitPrice: this.order.orders[0][vSOPrice],
      targetPrice: this.order.orders[0][vprice],
      titles: this.order.orders[0].quantity,
      vigency: this.order.duration,
      type: 'One cancels another (OCA)',
      isBuy: this.bitBuy,
      typeId: ORDER_TYPE_LIST.oca,
      algorithm: 'Stop limitado',
      algorithmTypeId: algorithm,
    };
  }

  tryStopLimitado(orderType: string, algorithm: any) {
    if (this.isStopLimitadoReady()) {
      this.buildStopLimitadoOrder(orderType, algorithm);
      this.setShowConfirmation(true);
    } else {
      this.setShowConfirmation(false);
    }
  }

  isTrailingStopReady(onlyCheck: boolean = false) {
    const parent = this.el.nativeElement;

    if (this.selectedOrder.id !== this.TRAILING_STOP) {
      return true;
    } else if (
      !this.isDefaultReady(onlyCheck) ||
      !this.isValidPrice(onlyCheck, this.porcentaje, '.porcentaje')
    ) {
      if (
        parent.querySelector('.btn-compra') !== null &&
        parent.querySelector('.btn-venta') !== null
      ) {
        parent.querySelector('.btn-compra').classList.remove('active');
        parent.querySelector('.btn-venta').classList.remove('active');
      }
      return false;
    } else {
      parent.querySelector('.btn-compra').classList.add('active');
      if (this.hasIssue || this.isPositionMocked) {
        parent.querySelector('.btn-venta').classList.add('active');
      }
      return true;
    }
  }

  buildTrailingStopOrder(orderType: string, algorithm: any) {
    const CAPITAL_ORDER: number =
      orderType === 'venta' ? ORDER_TYPE.VENTANORMAL : ORDER_TYPE.COMPRANORMAL;
    const ALGOTRADING: string =
      orderType === 'venta' ? 'SOAlgoTradingTypeId' : 'FOAlgoTradingTypeId';
    const PERCENTAGE: string =
      orderType === 'venta' ? 'SOStopPercentage' : 'stopPercentage';
    const STOP: string = orderType === 'venta' ? 'SOStop' : 'stop';
    const PRICE: string = orderType === 'venta' ? 'price' : 'SOPrice';

    this.order = {
      contractId: this.currentContract,
      duration: this.duration,
      algoTradingTypeId: ALGORITHM_TYPE.ONECANCELANOTHER,
      orders: [
        {
          issueId: this.currentIssue.issueID,
          quantity: this.titles,
          PRICE: this.price,
          capitalOrderTypeId: CAPITAL_ORDER,
          instrumentType: this.currentIssue.instrumentType,
          algoTradingTypeId: ALGORITHM_TYPE.ONECANCELANOTHER,
        },
      ],
    };
    this.order.orders[0][ALGOTRADING] = algorithm;
    this.order.orders[0][PERCENTAGE] = this.porcentaje;

    this.confirmationOrder = {
      issueID: this.order.orders[0].issueId,
      issueName: this.currentIssue.issueName,
      stopPercentage: this.order.orders[0][PERCENTAGE],
      targetPrice: this.order.orders[0].PRICE,
      titles: this.order.orders[0].quantity,
      vigency: this.order.duration,
      type: 'One cancels another (OCA)',
      isBuy: this.bitBuy,
      typeId: ORDER_TYPE_LIST.oca,
      algorithm: 'Trailing stop',
      algorithmTypeId: algorithm,
    };
  }

  tryTrailingStop(orderType: string, algorithm: any) {
    if (this.isTrailingStopReady()) {
      this.buildTrailingStopOrder(orderType, algorithm);
      this.setShowConfirmation(true);
    } else {
      this.setShowConfirmation();
    }
  }

  tryBuy() {
    if (this.isOrderReady()) {
      const operationCallback = () => {
        this.saleType = 'compra';
        this.bitBuy = true;
        this._getChallenge();

        switch (this.selectedOrder.id) {
          case this.STOP_EN_LINEA:
            this.tryStopEnLinea('compra', ALGORITHM_TYPE.STOPMARKET);
            break;
          case this.STOP_LIMITADO:
            this.tryStopLimitado('compra', ALGORITHM_TYPE.STOPLIMIT);
            break;
          case this.TRAILING_STOP:
            this.tryTrailingStop('compra', ALGORITHM_TYPE.TRAILINGSTOP);
            break;
          default:
        }
      };

      if (this.shouldNotifyGeolocationOnOperationAttempt) {
        this.notificationService.showInfo({
          message: GEOLOCATION_NOTIFICATION_MESSAGE,
        });
        return;
      }

      if (this.shouldStartLoaderOnOperationAttempt) {
        /**
         * TODO: Remove the commented lines when ready to display
         * loading buttons in the operation puppet
         */
        // this.isBuyButtonLoading = true;
        // this.isSellButtonLoading = false;
        this.operationCallback = operationCallback;
        return;
      }

      operationCallback();
    } else {
      this.setShowConfirmation();
    }
  }

  trySell() {
    if (!this.hasIssue && !this.isPositionMocked) {
      return false;
    }
    if (this.isOrderReady()) {
      const operationCallback = () => {
        this.saleType = 'venta';
        this.bitBuy = false;
        this._getChallenge();

        switch (this.selectedOrder.id) {
          case this.STOP_EN_LINEA:
            this.tryStopEnLinea('venta', ALGORITHM_TYPE.STOPMARKET);
            break;
          case this.STOP_LIMITADO:
            this.tryStopLimitado('venta', ALGORITHM_TYPE.STOPLIMIT);
            break;
          case this.TRAILING_STOP:
            this.tryTrailingStop('venta', ALGORITHM_TYPE.TRAILINGSTOP);
            break;
          default:
        }
      };

      if (this.shouldNotifyGeolocationOnOperationAttempt) {
        this.notificationService.showInfo({
          message: GEOLOCATION_NOTIFICATION_MESSAGE,
        });
        return;
      }

      if (this.shouldStartLoaderOnOperationAttempt) {
        /**
         * TODO: Remove the commented lines when ready to display
         * loading buttons in the operation puppet
         */
        // this.isBuyButtonLoading = false;
        // this.isSellButtonLoading = true;
        this.operationCallback = operationCallback;
        return;
      }

      operationCallback();
    } else {
      this.setShowConfirmation();
    }
    this._getChallenge();
  }

  setShowConfirmation(toShow: boolean = false) {
    this.showConfirmation = toShow;
    this.lockConfirmation = this.isReadAndWrite ? false : true;

    if (!this.showConfirmation) {
      this.token = '';
    } else {
      this.tradeInfo = new TradeInfo(
        false,
        this.currentIssue,
        TrackingOrderType.OneCancelsAnother,
        window.location.pathname,
        this.titles.toString(),
        this.saleType,
        (this.price * this.titles).toString(),
        this.price.toString(),
        this.order.duration,
      );

      const startTradeInfoData = this.tradeInfo.getAnaliticsTradeInfo();

      this.analyticsTrackingService.logEvent(
        TRACKING_EVENTS.start_trade.event,
        startTradeInfoData,
      );
    }

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  cancelConfirmation() {
    this.showConfirmation = false;
    this.lockConfirmation = false;
    this.token = '';

    this.store.dispatch({
      type: OPERATION_MODAL_OPENED,
      payload: { isModalOpened: this.showConfirmation },
    });
  }

  registerOrder() {
    const startTime: any = new Date();
    const hash: any = this.newRelicLogger.buildHash(
      startTime.getTime(),
      this.order.contractId,
      this.currentIssue.issueID,
      this.titles,
      this.order.orders[0].capitalOrderTypeId,
    );

    this.order.orders[0].hash = hash;

    this.operationService
      .registerCapitalOrder(this.order)
      .pipe(
        catchError((error: OperationError) => {
          const customAttributes = {
            _type: 'OCA',
            contractId: this.currentContract,
          };

          this.newRelicLogger.setEvent(
            NEW_RELIC_KNOWN_ERRORS.tradeError,
            error.toJSON(),
            customAttributes,
          );

          this.reset();

          this.tradeInfo.setSuccess(false);
          this.tradeInfo.setCallback(error.message);
          this.tradeInfo.setContractId(this.currentContract);

          const tradeErrorInfoData = this.tradeInfo.getAnaliticsTradeInfo();
          this.tradeInfo.setContractId('');

          this.analyticsTrackingService.logEvent(
            TRACKING_EVENTS.trade.event,
            tradeErrorInfoData,
          );
          return EMPTY;
        }),
      )
      .subscribe((value: any) => {
        const orderId: number =
          value.electronicOrderId !== undefined && value.electronicOrderId > 0
            ? value.electronicOrderId
            : value.vigenciaId;

        this.reset();
        this.notificationService.showSuccess({
          title: 'Orden Ejecutada',
          message: `La orden ${orderId} fue enviada correctamente.`,
          eventId: undefined,
          isBusinessError: false,
        });

        this.tradeInfo.setSuccess(true);
        this.tradeInfo.setCallback(GENERAL_CODE_CALLBACKS.SUCCESS);
        const tradeSuccessInfoData = this.tradeInfo.getAnaliticsTradeInfo();

        this.analyticsTrackingService.logEvent(
          TRACKING_EVENTS.trade.event,
          tradeSuccessInfoData,
        );
      });
  }

  doMovement() {
    if (this.lockConfirmation) {
      return;
    }
    this.lockCancel = true;
    this.lockConfirmation = true;
    if (!this.isReadAndWrite && this.token.length !== 0) {
      this.updateSession(this.token);
    } else {
      this.registerOrder();
    }
  }

  private _getChallenge() {
    if (this.isReadAndWrite) {
      this.setShowConfirmation(true);
    } else {
      this.patronService
        .getChallenge()
        .pipe(
          catchError((error: any) => {
            this.sendOperativeMessage('Error', error.message);

            this.lockConfirmation = false;
            this.lockCancel = false;
            return observableOf(error);
          }),
        )
        .subscribe((value: any) => {
          this.setShowConfirmation(true);
        });
    }
  }

  /**
   * Update session when token is required
   */
  private updateSession(token: string) {
    this.patronService
      .upgradeToken(
        this.elPatronData.URL_UPGRADE_SESSION,
        this.readToken,
        token,
      )
      .pipe(
        catchError((error: Error) => {
          this.sendOperativeMessage('Token incorrecto', error.message);

          return EMPTY;
        }),
      )
      .subscribe(() => this.registerOrder());
  }
}
